// Moeda de pagamento da parcela da Previsão (D-0117, Rafael 27/10): a operadora informa em qual moeda a parcela
// será paga e o sistema recebe como verdade o câmbio da etapa vigente do fornecedor (Fechamento > Negociado > Pré).
// Opções = moedas do fornecedor no orçamento + R$ (API: `moedas_parcela`, mesma régua da trava do servidor).
// Fornecedor com mais de uma moeda: uma parcela por moeda. Parcela sem `moeda_origem` = legado (só R$).
import { moeda } from '../../util/util';

const OPCAO_REAL = { moeda: 'real', simbolo: 'R$', cambio: 1, orcado: 0 };
const ETAPA_ROTULO = { Fechamento: 'Fechamento', Negociado: 'Negociado', PreEvento: 'Pré-evento' };

const centavos = (v) => Math.round(Number(v || 0) * 100);
const formatarCambio = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 6 });

// Opções do campo "Moeda da parcela". Sem o campo (API antiga) = só R$.
export function opcoesMoedaParcela(forn = {}) {
    const lista = Array.isArray(forn && forn.moedas_parcela) ? forn.moedas_parcela.filter(o => o && o.moeda) : [];
    return lista.some(o => o.moeda === 'real') ? lista : [...lista, OPCAO_REAL];
}

export function opcaoMoeda(opcoes = [], chave = 'real') {
    return opcoes.find(o => o.moeda === chave) || null;
}

// Moeda inicial do modal: a da parcela em edição; parcela nova de fornecedor só estrangeiro (sem nada em R$
// no orçamento) já vem na moeda dele; nos demais casos, R$.
export function moedaInicialParcela(forn = {}, parcela = null) {
    const opcoes = opcoesMoedaParcela(forn);
    if (parcela) {
        const m = parcela.moeda_origem;
        return m && opcaoMoeda(opcoes, m) ? m : (m || 'real');
    }
    const estrangeiras = opcoes.filter(o => o.moeda !== 'real');
    const real = opcaoMoeda(opcoes, 'real');
    return estrangeiras.length === 1 && !(Number(real && real.orcado) > 0) ? estrangeiras[0].moeda : 'real';
}

// R$ da parcela = valor na moeda × câmbio da etapa (a mesma conta da API).
export function valorBrlDaParcela(valorOrigem, opcao) {
    if (valorOrigem === '' || valorOrigem === null || valorOrigem === undefined || !opcao) return '';
    return Math.round(Number(valorOrigem) * Number(opcao.cambio || 0) * 100) / 100;
}

// Saldo na moeda estrangeira: orçado nela − parcelas de previsão já lançadas nela (a parcela em edição sai da soma).
// Parcela legada (sem moeda) não conta aqui — ela segue na trava do total em R$. R$ não tem saldo próprio.
export function saldoNaMoeda(forn = {}, opcao = null, idEditando = null) {
    if (!opcao || opcao.moeda === 'real') return null;
    const parcelas = (forn && (forn.parcelas_aprovadas || forn.parcelas)) || [];
    const parcelado = parcelas
        .filter(p => String(p.id) !== String(idEditando) && p.moeda_origem === opcao.moeda)
        .reduce((s, p) => s + Number(p.valor_origem || 0), 0);
    const orcado = Number(opcao.orcado || 0);
    return { moeda: opcao.moeda, simbolo: opcao.simbolo, orcado, parcelado: Math.round(parcelado * 100) / 100,
        saldo: Math.round((orcado - parcelado) * 100) / 100 };
}

export function excedeNaMoeda(valorOrigem, saldo) {
    if (!saldo) return false;
    return centavos(valorOrigem) > centavos(saldo.saldo);
}

// Na moeda estrangeira o R$ é arredondado por parcela: 1 centavo de folga no total em R$ (igual à API);
// quem segura o valor é a trava na moeda.
export function excedeSaldoBrl(valorBrl, saldoBrl, emMoedaEstrangeira) {
    return centavos(valorBrl) > centavos(saldoBrl) + (emMoedaEstrangeira ? 1 : 0);
}

// "Usar saldo" na moeda: o menor entre o saldo na moeda e o saldo em R$ convertido.
export function saldoUsavelNaMoeda(saldoMoeda, saldoBrl, opcao) {
    if (!saldoMoeda || !opcao || !(Number(opcao.cambio) > 0)) return 0;
    const peloBrl = Math.floor(Number(saldoBrl || 0) / Number(opcao.cambio) * 100) / 100;
    return Math.max(0, Math.min(saldoMoeda.saldo, peloBrl));
}

export function rotuloEtapa(etapa) {
    return ETAPA_ROTULO[etapa] || etapa || '';
}

// Rótulo curto do seletor: "US$ · 5,2000" / "R$ (Real)" (a etapa aparece na linha do R$, embaixo do valor).
export function rotuloOpcaoMoeda(opcao) {
    if (!opcao) return '';
    return opcao.moeda === 'real' ? 'R$ (Real)' : opcao.simbolo + ' · ' + formatarCambio(opcao.cambio);
}

// "câmbio do Negociado 5,2000" — de onde vem a conversão do modal.
export function textoCambioDaEtapa(opcao) {
    if (!opcao || opcao.moeda === 'real') return '';
    const etapa = rotuloEtapa(opcao.etapa);
    return 'câmbio ' + (etapa ? 'do ' + etapa + ' ' : '') + formatarCambio(opcao.cambio);
}

// Parcela ainda não aprovada com câmbio diferente do da etapa atual: avisa (a API refaz ao editar). Aprovada
// com a cotação do banco (brl_fora_cambio) ou já no Financeiro fica congelada e não avisa.
export function avisoCambioDesatualizado(parcela = {}, forn = {}) {
    const m = parcela.moeda_origem;
    if (!m || m === 'real' || parcela.aprovada_financeiro || parcela.liberado_financeiro_at) return null;
    if (Number(parcela.brl_fora_cambio || 0) > 0 || (forn && forn.cambio_banco)) return null;
    const opcao = opcaoMoeda(opcoesMoedaParcela(forn), m);
    if (!opcao) return 'A moeda desta parcela não está mais no orçamento do fornecedor. Edite a parcela.';
    if (Math.round(Number(parcela.cambio_origem || 0) * 1e6) === Math.round(Number(opcao.cambio || 0) * 1e6)) return null;
    const novo = valorBrlDaParcela(parcela.valor_origem, opcao);
    return 'O ' + textoCambioDaEtapa(opcao) + ' mudou (parcela em ' + formatarCambio(parcela.cambio_origem)
        + '): ficaria R$ ' + moeda(novo) + '. Edite e salve a parcela para atualizar.';
}
